import { apiGet, apiPost, apiPut, apiDelete } from '../../../lib/api';
import { ModuleConfigs } from '../components/moduleConfigDefs';

export interface ConfigurationData extends Partial<ModuleConfigs> {
  id?: string;
  name: string;
  description?: string;
  hardware_config: {
    unitCost: number;
    utilization: number;
    profitMargin: number;
    rentalRate: number;
    routerUnitCost?: number;
    starlinkHardwareCost?: number;
    starlinkMonthlyCost?: number;
    // Manual welding hardware. Undefined means "not set": the shared values
    // above are used for Manual units too.
    manualUnitCost?: number | null;
    manualUtilization?: number | null;
    manualProfitMargin?: number | null;
    manualRentalRate?: number | null;
  };
  application_config: {
    payBackPeriod: number;
    developmentCost: number;
    profitMargin: number;
    unitsPerYear: number;
  };
  duration_discount_config: {
    lessThan12Month: number;
    month13To24: number;
    month25To36: number;
    month37To60: number;
    perpetual: number;
  };
  other_costs_config: {
    internetFee: number;
    awsCompute: number;
    awsStorage: number;
    liveModeInternetCloudMultiplier: number;
    offlineModeInternetCloudMultiplier: number;
    defaultSparePricingMode: 'hw_only' | 'hw_sw' | 'hw_sw_other';
    allowSparePricingModeOverride: boolean;
    maintenanceAndSupport: number;
    trainingRate: number;
    trainingQuickHours: number;
    trainingMediumHours: number;
    trainingLongHours: number;
    provisioningHourlyRate: number;
    onFieldSupportDailyRate: number;
    unitWeightKg: number;
    shippingCostPerKg: number;
  };
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface ApiResponse<T> {
  statusCode: number;
  body: T;
}

export const configurationService = {
  async getCurrentConfiguration(): Promise<ConfigurationData> {
    const result = await apiGet<ApiResponse<ConfigurationData>>('/configurations/current');
    return result.body;
  },

  async getConfigurations(): Promise<ConfigurationData[]> {
    const result = await apiGet<ApiResponse<ConfigurationData[]>>('/configurations');
    return result.body;
  },

  async getConfiguration(id: string): Promise<ConfigurationData> {
    const result = await apiGet<ApiResponse<ConfigurationData>>(`/configurations/${id}`);
    return result.body;
  },

  async createConfiguration(data: ConfigurationData): Promise<ConfigurationData> {
    const result = await apiPost<ApiResponse<ConfigurationData>>('/configurations', data);
    return result.body;
  },

  async updateConfiguration(id: string, data: Partial<ConfigurationData>): Promise<ConfigurationData> {
    const result = await apiPut<ApiResponse<ConfigurationData>>(`/configurations/${id}`, data);
    return result.body;
  },

  async deleteConfiguration(id: string): Promise<void> {
    await apiDelete(`/configurations/${id}`);
  },
};
